import { API_CONFIG, API_ENDPOINTS } from './config';
import { apiClient } from './client';
import { getOrganizationId } from './organization';
import { getAiAnalyticsBaseUrl } from './aiAnalyticsUrl';

export const MIN_AI_CREDITS_REQUIRED = 0.3;

type AiCreditsCheckResult = {
  allowed: boolean;
  available: number;
  required: number;
  reason?: string;
};

type EnsureAiCreditsOptions = {
  businessAccountId?: string | null;
  organizationId?: string | null;
  requiredCredits?: number;
};

const CACHE_TTL_MS = 15_000;

// Query key of the dashboard-stats request that backs the header AI-credits badge.
// Exported so anything that spends credits invalidates the same key the badge reads,
// instead of both sides hardcoding the string and drifting apart.
export const AI_CREDITS_STATS_QUERY_KEY = ['topnav-dashboard-stats'] as const;

let cachedCheck:
  | {
      key: string;
      available: number;
      checkedAt: number;
    }
  | null = null;

/**
 * Drop the cached balance so the next guard check re-reads it.
 * Call after anything that spends credits — otherwise the guard keeps serving a
 * pre-spend balance for up to CACHE_TTL_MS.
 */
export const resetAiCreditsCache = () => {
  cachedCheck = null;
};

const getSelectedBusinessAccountId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('selectedBusinessAccountId');
  } catch {
    return null;
  }
};

const resolveAiCredits = async (
  businessAccountId?: string | null,
  organizationId?: string | null
): Promise<number> => {
  const orgId = organizationId || getOrganizationId();
  // Fail-open for anonymous/public contexts where organization is unknown.
  if (!orgId) return Number.POSITIVE_INFINITY;

  const selectedBusinessId = businessAccountId || getSelectedBusinessAccountId();
  const cacheKey = `${orgId}::${selectedBusinessId || 'none'}`;
  const now = Date.now();

  if (
    cachedCheck &&
    cachedCheck.key === cacheKey &&
    now - cachedCheck.checkedAt <= CACHE_TTL_MS
  ) {
    return cachedCheck.available;
  }

  const stats = await apiClient.get(API_ENDPOINTS.DASHBOARD_STATS, {
    params: {
      organization_id: orgId,
      ...(selectedBusinessId ? { business_account_id: selectedBusinessId } : {}),
    },
  });

  if (stats.error) {
    throw new Error(stats.error || 'Unable to validate AI credits');
  }

  const available = Number((stats.data as any)?.aiCredits?.available ?? 0);
  cachedCheck = { key: cacheKey, available, checkedAt: now };
  return available;
};

export const checkAiCreditsForTrigger = async (
  options: EnsureAiCreditsOptions = {}
): Promise<AiCreditsCheckResult> => {
  const required = Number(options.requiredCredits ?? MIN_AI_CREDITS_REQUIRED);
  const available = await resolveAiCredits(
    options.businessAccountId,
    options.organizationId
  );

  if (!Number.isFinite(available)) {
    // No org context (public pages) -> allow.
    return { allowed: true, available, required, reason: 'no_org_context' };
  }

  return {
    allowed: available > required,
    available,
    required,
    reason:
      available > required
        ? 'ok'
        // : `AI credits are low (${available.toFixed(2)}). Minimum required is ${required.toFixed(2)}.`,
        : `AI Credits are Expired. Please recharge to continue using AI features.`, // More user-friendly message
  };
};

export const ensureAiCreditsForTrigger = async (
  options: EnsureAiCreditsOptions = {}
): Promise<AiCreditsCheckResult> => {
  const result = await checkAiCreditsForTrigger(options);
  if (!result.allowed) {
    throw new Error(result.reason || 'Insufficient AI credits');
  }
  return result;
};

const isAiAnalyticsUrl = (rawUrl: string): boolean => {
  if (!rawUrl) return false;

  const apiBase = getAiAnalyticsBaseUrl('api');
  return rawUrl.startsWith(apiBase);
};

const isGuardExemptRead = (rawUrl: string, method?: string): boolean => {
  try {
    const url = new URL(rawUrl, window.location.origin);
    const path = url.pathname;

    // Ask AI (Echo AI navigation assistant) is always exempt — uses its own credit pool
    if (/\/api\/ask-ai\//i.test(path)) return true;

    const normalizedMethod = String(method || 'GET').toUpperCase();
    if (normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD') return false;

    return (
      /\/api\/ai-agent\/organization\/[^/]+\/agents$/i.test(path) ||
      /\/api\/ai-agent\/business\/[^/]+\/agents$/i.test(path) ||
      /\/api\/livechat\/organization\/[^/]+\/scripts$/i.test(path) ||
      /\/api\/livechat\/business\/[^/]+\/scripts$/i.test(path) ||
      /\/api\/livechat\/script\/[^/]+$/i.test(path)
    );
  } catch {
    return false;
  }
};

export const installAiAnalyticsFetchGuard = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

  const guardFlag = '__AI_FETCH_GUARD_INSTALLED__';
  if ((window as any)[guardFlag]) return;
  (window as any)[guardFlag] = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestUrl = typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.toString()
      : input.url;
    const requestMethod = init?.method || (typeof input !== 'string' && !(input instanceof URL) ? input.method : 'GET');

    if (isAiAnalyticsUrl(requestUrl) && !isGuardExemptRead(requestUrl, requestMethod)) {
      try {
        await ensureAiCreditsForTrigger();
      } catch (error: any) {
        const message = error?.message || 'AI credits are below the required threshold.';
        return new Response(
          JSON.stringify({
            error: message,
            blocked: true,
            min_required_credits: MIN_AI_CREDITS_REQUIRED,
          }),
          {
            status: 402,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      }
    }

    return originalFetch(input as any, init);
  };
};
